import { ChildModel, ModelBase } from 'store/model-base';
import { ChargeType } from 'my-account/constants';
import { LineItemPlan } from './plan';

export class LineItemCharge extends ModelBase {
    public static storeName: string = 'lineItemCharge';

    public name: string;
    public chargeCategory: ChargeType;
    public price: number;
    public planDetails: LineItemPlan;
    public usageDetail: LineItemUsage;
    public chargeSubcategory: string;
    public tipOfferId: string;
    public isLineServicePrimary: boolean;
    public isDeviceFinanced: boolean;
    public isInsurancePlan: boolean;
    public isXMCIncluded: boolean;
    public isNoWireline: boolean;
    public isDomesticCharge: boolean;
    public isGTP: boolean;
    public isMexCanUnlimitedCharge: boolean;
    public isDataRoaming: boolean;
    public isInternationalCharge: boolean;
    public isRoamingCharge: boolean;
    public isSubsidy: boolean;
    public isDeviceAccessFee: boolean;
    public hasSharedData: boolean;
    public isMexCanCharge: boolean;
    public isHdPassCharge: boolean;
    public isGtpCharge: boolean;
    public isGtpCanMexCharge: boolean;
    public isUnlCanMexCharge: boolean;
    public isGTPIncl: boolean;
    public isActivationFee: boolean;
    public isActivationFeeDiscount: boolean;
    public isTip: boolean;
    public isPromoService: boolean;
    public isXMCPromo: boolean;
    public isServicePromoLine: boolean;
    public isMexCanAdditionalPass: boolean;
    public isGtpAdditionalPass: boolean;
    public isDeliveryFee: boolean;
    public installmentPlanEndDate: string;
    public remainingBalance: number;
    public remainingInstallmentCount: number;
    public totalInstallmentCount: number;
    public lastInstallment: boolean;

    /**
     * Creates a new instance of the LineItemCharge model based on the provided ApiResponse data.
     * @param data - The ApiResponse data used to create the LineItemCharge instance.
     * @returns A new instance of the LineItemCharge model.
     */
    public static create<T extends ModelBase>(data: ApiResponse): T {
        if (!data.usageDetail) {
            data.usageDetail = {
                textVolume: 0,
                voiceVolume: 0,
                dataConsumed: 0
            };
        }

        data.isLineServicePrimary = data.chargeCategory?.toUpperCase() === ChargeType.LINE_SERVICE_PRIMARY;
        data.isDeviceFinanced = data.chargeCategory?.toUpperCase() === ChargeType.DEVICE_FINANCED;
        data.isInsurancePlan = data.chargeCategory?.toUpperCase() === ChargeType.INSURANCE_PLAN;
        data.isXMCIncluded = data.chargeCategory?.toUpperCase() === ChargeType.XMC_INCLUDED;
        data.isNoWireline = data.chargeCategory?.toUpperCase() === ChargeType.NO_WIRELINE_SURCHARGE;
        data.isDomesticCharge = data.chargeCategory?.toUpperCase() === ChargeType.DOMESTIC_CHARGE;
        data.isInternationalCharge = data.chargeCategory?.toUpperCase() === ChargeType.INTERNATIONAL_CHARGE;
        data.isRoamingCharge = data.chargeCategory?.toUpperCase() === ChargeType.ROAMING_CHARGE;
        data.isDeviceAccessFee = data.chargeCategory?.toUpperCase() === ChargeType.DEVICE_ACCESS_FEE;
        data.isSubsidy = data.chargeCategory?.toUpperCase() === ChargeType.DEVICE_SUBSIDY;
        data.isMexCanCharge = data.chargeCategory?.toUpperCase() === (ChargeType.MEX_CAN_PASS || ChargeType.CANMEX_UNLIMITED_PASS);
        data.isHdPassCharge = data.chargeCategory?.toUpperCase() === ChargeType.HD_PASS;
        data.isGtpCharge = data.chargeCategory?.toUpperCase() === ChargeType.GLOBAL_TRAVEL_PASS;
        data.isGtpCanMexCharge = data.chargeCategory?.toUpperCase() === ChargeType.MEX_CAN_TRAVEL_PASS;
        data.isGTP = data.chargeCategory?.toUpperCase() === ChargeType.GTP;
        data.isMexCanUnlimitedCharge = data.chargeCategory?.toUpperCase() === ChargeType.CANMEX_UNLIMITED_PASS;
        data.isDataRoaming = data.chargeCategory?.toUpperCase() === ChargeType.DATA_ROAMING;
        data.isActivationFee = data.chargeCategory?.toUpperCase() === ChargeType.ACTIVATION_FEE;
        data.isActivationFeeDiscount = data.chargeCategory?.toUpperCase() === ChargeType.ACTIVATION_FEE_DISCOUNT;
        data.isTip = data.chargeSubcategory?.toUpperCase() === (ChargeType.TIP || ChargeType.TRADEIN_PROMO_DDA);
        data.isUnlCanMexCharge = data.chargeCategory?.toUpperCase() === ChargeType.MEX_CAN_UNL;
        data.isGTPIncl = data.chargeCategory?.toUpperCase() === ChargeType.GTP_INCL;
        data.isPromoService = data.chargeCategory?.toUpperCase() === ChargeType.SERVICE_PROMO;
        data.isXMCPromo = data.chargeCategory?.toUpperCase() === ChargeType.XMC_PROMO;
        data.isServicePromoLine = data.chargeCategory?.toUpperCase() === ChargeType.SERVICE_PROMO_LINE;
        data.isMexCanAdditionalPass = data.chargeCategory?.toUpperCase() === ChargeType.CANMEX_UNL_ADDITIONAL_PASS;
        data.isGtpAdditionalPass = data.chargeCategory?.toUpperCase() === ChargeType.GLOBAL_TRAVEL_ADDITIONAL_PASS;
        data.isDeliveryFee = data.chargeCategory?.toUpperCase() === ChargeType.DELIVERY_FEE;

        const toReturn: LineItemCharge = super.create<LineItemCharge>(data);
        toReturn.hasSharedData = Boolean(toReturn.planDetails.hasSharedData);

        /* eslint-disable @typescript-eslint/no-explicit-any */
        return <T> <any> toReturn;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'planDetails',
            model: LineItemPlan
        }];
    }
}
